import React, { useCallback, useEffect, useRef, useState } from 'react';
import { readWorkbook } from '../utils/xlsx';
import { parseRuleWorkbook, buildImportPayload } from '../utils/ppRuleWorkbook';
import paymentporterService from '../services/paymentporterService';
import './PpRuleImportModal.css';

// Bulk rule upload. Three steps, and the middle one is the point: the workbook is parsed and validated in
// the browser and shown back as a preview, so nothing is written until the author has seen exactly which
// rules — and which criteria — they are about to create. Reuses the .pp-modal-* shell from
// PaymentPorterPage.css.

const CriteriaChips = ({ rule }) => {
    const chips = [];
    if (rule.rateCodes.length)         chips.push(`${rule.rateCodes.length} rate code${rule.rateCodes.length > 1 ? 's' : ''}`);
    if (rule.corporateProfiles.length) chips.push(`${rule.corporateProfiles.length} corporate`);
    if (rule.iataProfiles.length)      chips.push(`${rule.iataProfiles.length} IATA`);
    if (rule.paymentMethods.length)    chips.push(`${rule.paymentMethods.length} payment method${rule.paymentMethods.length > 1 ? 's' : ''}`);
    if (rule.routing)                  chips.push(`routing ${rule.routing.code}${rule.routing.default_window ? ` → window ${rule.routing.default_window}` : ''}`);
    if (rule.profileAssignment)        chips.push(`profile ${rule.profileAssignment.profile_id}`);
    if (!chips.length) return <span className="pri-chip pri-chip-warn">no criteria</span>;
    return <>{chips.map((c, i) => <span key={i} className="pri-chip">{c}</span>)}</>;
};

const PpRuleImportModal = ({ ppId, existingRules = [], onClose, onImported }) => {
    const [fileName, setFileName]   = useState(null);
    const [parsing, setParsing]     = useState(false);
    const [parsed, setParsed]       = useState(null);   // { rules, errors, conflicts }
    const [fatal, setFatal]         = useState(null);   // the file itself could not be read
    const [importing, setImporting] = useState(false);
    const [result, setResult]       = useState(null);
    const [dragging, setDragging]   = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape' && !importing) onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose, importing]);

    const handleFile = useCallback(async (file) => {
        if (!file) return;
        setFileName(file.name);
        setParsed(null); setFatal(null); setResult(null); setParsing(true);
        try {
            const book = await readWorkbook(file);
            setParsed(parseRuleWorkbook(book, existingRules));
        } catch (err) {
            setFatal(err.message || 'The file could not be read.');
        } finally {
            setParsing(false);
        }
    }, [existingRules]);

    const handleImport = async () => {
        setImporting(true);
        try {
            const res = await paymentporterService.importRules(ppId, buildImportPayload(parsed.rules));
            setResult(res.data || {});
            onImported?.();
        } catch (err) {
            setFatal(err.response?.data?.error?.message || err.response?.data?.message || err.message || 'Import failed');
        } finally {
            setImporting(false);
        }
    };

    // A name clash rejects the whole file server-side, so it blocks the button here too — offering
    // "Import 7 rules" when the server will refuse all 8 would be a lie about what happens next.
    const canImport = parsed && !parsed.errors.length && !parsed.conflicts.length && parsed.rules.length > 0;

    return (
        <div className="pp-modal-backdrop" onClick={() => !importing && onClose()}>
            <div className="pp-modal pri-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
                <div className="pp-modal-header">
                    <span>Upload Rules from Excel</span>
                    <button className="pp-modal-close" onClick={onClose} disabled={importing}>×</button>
                </div>

                <div className="pri-body">
                    {result ? (
                        <div className="pri-done">
                            <div className="pri-done-title">
                                Imported {result.created} rule{result.created === 1 ? '' : 's'}
                            </div>
                            <p className="pri-done-note">
                                Every imported rule is <strong>inactive</strong>. Open each one, run Test → Details → Dry Run
                                to confirm it behaves as expected, then switch it on.
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Step 1 — pick the file */}
                            <div
                                className={`pri-drop${dragging ? ' pri-drop-active' : ''}`}
                                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                                onDragLeave={() => setDragging(false)}
                                onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
                                onClick={() => inputRef.current?.click()}
                            >
                                <input
                                    ref={inputRef}
                                    type="file"
                                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                    style={{ display: 'none' }}
                                    onChange={e => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
                                />
                                <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 16V4M8 8l4-4 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                                </svg>
                                <span className="pri-drop-main">{fileName || 'Drop your .xlsx here, or click to choose a file'}</span>
                                <span className="pri-drop-sub">Use the example workbook from “Download Example” so the sheet and column names match.</span>
                                {/* Stated before the file is chosen, not only in the rejection: this is the one
                                    rule that fails the ENTIRE import rather than skipping a row, so finding out
                                    about it from an error after building a 60-rule sheet is too late. */}
                                <span className="pri-drop-sub">
                                    A rate code, payment method or profile id already used by a rule on this porter
                                    rejects the whole file — nothing is created.
                                </span>
                            </div>

                            {parsing && <div className="pri-status">Reading the workbook…</div>}
                            {fatal && <div className="pri-error-block">{fatal}</div>}

                            {/* Step 2 — what was found */}
                            {parsed && !parsing && (
                                <>
                                    {parsed.errors.length > 0 && (
                                        <div className="pri-error-block">
                                            <strong>
                                                {parsed.errors.length} problem{parsed.errors.length === 1 ? '' : 's'} found — nothing has been imported.
                                            </strong>
                                            <ul className="pri-error-list">
                                                {parsed.errors.slice(0, 40).map((e, i) => (
                                                    <li key={i}><span className="pri-error-where">{e.where}</span> {e.message}</li>
                                                ))}
                                            </ul>
                                            {parsed.errors.length > 40 && (
                                                <div className="pri-error-more">…and {parsed.errors.length - 40} more.</div>
                                            )}
                                            <div className="pri-error-fix">Fix them in the workbook and drop the file again.</div>
                                        </div>
                                    )}

                                    {parsed.conflicts.length > 0 && (
                                        <div className="pri-error-block">
                                            <strong>
                                                {parsed.conflicts.length} rule name{parsed.conflicts.length === 1 ? '' : 's'} already used on
                                                this porter — nothing can be imported.
                                            </strong>
                                            <div>
                                                {parsed.conflicts.join(', ')} — an upload is never merged into an existing rule, because
                                                that could widen what an already-active rule matches. Rename them in the workbook, or
                                                remove them from the file, and upload again.
                                            </div>
                                        </div>
                                    )}

                                    {parsed.rules.length > 0 && (
                                        <div className="pri-preview">
                                            <div className="pri-preview-head">
                                                {parsed.rules.length} rule{parsed.rules.length === 1 ? '' : 's'} in this file
                                                {parsed.conflicts.length > 0 && ' · blocked by a name clash'}
                                            </div>
                                            <table className="pp-modal-table pri-table">
                                                <thead>
                                                    <tr><th>Rule</th><th>Seq</th><th>Configuration</th></tr>
                                                </thead>
                                                <tbody>
                                                    {parsed.rules.map((r, i) => (
                                                        <tr key={i} className={r.__conflict ? 'pri-row-skip' : ''}>
                                                            <td>
                                                                <span className="pri-rule-name">{r.name}</span>
                                                                {r.__conflict && <span className="pri-chip pri-chip-warn">name already exists</span>}
                                                                {r.description && <div className="pri-rule-desc">{r.description}</div>}
                                                            </td>
                                                            <td>{r.sequence ?? '—'}</td>
                                                            <td><CriteriaChips rule={r} /></td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>

                <div className="pri-footer">
                    {!result && canImport && (
                        <span className="pri-footer-note">
                            Imported rules are created inactive — nothing runs against the PMS until you switch them on.
                        </span>
                    )}
                    <button className="pp-run-btn" onClick={onClose} disabled={importing}>
                        {result ? 'Close' : 'Cancel'}
                    </button>
                    {!result && (
                        <button className="pp-rules-add-btn" onClick={handleImport} disabled={!canImport || importing}>
                            {importing ? 'Importing…' : `Import ${parsed?.rules.length || ''} rule${parsed?.rules.length === 1 ? '' : 's'}`}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PpRuleImportModal;
